import Card from '@mui/material/Card';
import { Box, Grid2, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  EventDiscriminator,
  IEventSimpleProps,
} from '../../../services/Events/types';
import Chip from '@mui/material/Chip';
import { useGetFaceRecognitionEventByIdAsync } from '../../../services/Events/FaceRecognitionEventService/hooks/useMutation';
import { useGetIvsEventByIdAsync } from '../../../services/Events/IvsEventsService/hooks/useMutation';
import { useGetStudentRecognitionEventByIdAsync } from '../../../services/Events/StudentRecognitionEventsService/hooks/useMutation';
import { useGetResponsibleRecognitionEventByIdAsync } from '../../../services/Events/ResponsibleRecognitionEventsService/hooks/useMutation';
import { useGetLprEventByIdAsync } from '../../../services/Events/LprEventsService/hooks/useMutation';
import { t } from 'i18next';
import { useFirebaseImageMutateFromUrl } from '../../../firebase';

interface LastEventProps {
  title: string;
  event: IEventSimpleProps | undefined;
  onClickInImage?: (imageUrl: string) => void;
}

interface IEventDetailsList {
  label: string;
  value: any;
}

const LastEvent = ({ event, onClickInImage, title }: LastEventProps) => {
  const [imageToRender, setImageToRender] = useState<string>();
  const [eventDetails, setEventDetails] = useState<any>();

  const { mutateAsync: mutateGetFaceRecognitionEventAsync } =
    useGetFaceRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetLprEventAsync } = useGetLprEventByIdAsync();
  const { mutateAsync: mutateGetResponsibleEventAsync } =
    useGetResponsibleRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetStudentEventAsync } =
    useGetStudentRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetIvsEventAsync } = useGetIvsEventByIdAsync();
  const [isVisibleEditionDialog, setIsVisibleEditionDialog] =
    useState<boolean>(false);

  const renderEventType = (eventType: EventDiscriminator) => {
    let label = '';
    switch (eventType) {
      case 'FaceRecognitionEventEntity':
        label = 'Reconhecimento facial';
        break;
      case 'LprEventEntity':
        label = 'Reconhecimento de placa';
        break;
      case 'IvsEventEntity':
        label = 'Cruzamento de linha';
        break;
      case 'StudentRecognitionEventEntity':
        label = 'Reconhecimento de Aluno';
        break;
      case 'ResponsibleRecognitionEventEntity':
        label = 'Reconhecimento de Responsável';
    }
    return <Chip label={label} sx={{ fontWeight: 'bold' }} color="primary" />;
  };
  const { mutate: loadImage, isLoading: isLoadingImage } =
    useFirebaseImageMutateFromUrl();
  const createLprDetails = (details: IEventDetailsList[]) => {
    for (const prop in eventDetails) {
      if (eventDetails[prop]) {
        if (prop === 'plate')
          details.push({
            label: t('common.plate'),
            value: eventDetails[prop],
          });
      }
    }
    details.push({
      label: t('common.date'),
      value: eventDetails.createdAt,
    });
  };

  const createResponsableDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: t('common.name'),
        value: eventDetails.clientName,
      });
    }
    details.push({
      label: t('common.date'),
      value: eventDetails.createdAt,
    });
  };

  const createStudentDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: t('common.name'),
        value: eventDetails.clientName,
      });
    }
    details.push({
      label: t('common.time'),
      value: eventDetails.createdAt,
    });
  };

  const createFacialDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: t('common.time'),
        value: eventDetails.createdAt,
      });
    }
  };

  const getValuesToRenderByEventType = (): any[] => {
    const details: IEventDetailsList[] = [];
    if (event?.id && eventDetails) {
      for (const prop in eventDetails) {
        if (eventDetails[prop]) {
          if (prop === 'description') {
            details.push({
              label: t('common.description'),
              value: eventDetails[prop],
            });
          }
        }
      }

      switch (event.discriminator) {
        case 'FaceRecognitionEventEntity':
          createFacialDetails(details);
          break;
        case 'LprEventEntity':
          createLprDetails(details);
          break;
        case 'IvsEventEntity':
          break;
        case 'StudentRecognitionEventEntity':
          createStudentDetails(details);
          break;
        case 'ResponsibleRecognitionEventEntity':
          createResponsableDetails(details);
          break;
      }
    }
    return details;
  };

  const renderEventDetails = () => {
    const valuesToRender: IEventDetailsList[] = getValuesToRenderByEventType();

    return valuesToRender.map((item, index) => (
      <Box
        key={index}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        justifyContent={'start'}
      >
        <Box>
          <Typography
            sx={{ fontWeight: 'bold', color: '#4967a6', margin: '3px' }}
          >
            {item.label}
          </Typography>
        </Box>
        <Box>
          <Typography fontSize={'13px'}>{item.value}</Typography>
        </Box>
      </Box>
    ));
  };

  useEffect(() => {
    if (event?.id) {
      switch (event.discriminator) {
        case 'FaceRecognitionEventEntity':
          mutateGetFaceRecognitionEventAsync(event?.id).then(setEventDetails);
          break;
        case 'LprEventEntity':
          mutateGetLprEventAsync(event?.id).then(setEventDetails);
          break;
        case 'IvsEventEntity':
          mutateGetIvsEventAsync(event?.id).then(setEventDetails);
          break;
        case 'StudentRecognitionEventEntity':
          mutateGetStudentEventAsync(event?.id).then(setEventDetails);
          break;
        case 'ResponsibleRecognitionEventEntity':
          mutateGetResponsibleEventAsync(event?.id).then(setEventDetails);
          break;
      }
    }
  }, [event]);

  useEffect(() => {
    if (!event) return;

    let publicUrl = '';

    if (event.carImage?.includes('https')) {
      publicUrl = event.carImage;
    } else if (event.recognitionImage?.includes('https')) {
      publicUrl = event.recognitionImage;
    } else if (event.image?.includes('https')) {
      publicUrl = event.image;
    }

    if (!publicUrl) {
      setImageToRender(undefined);
      return;
    }

    loadImage(publicUrl, {
      onSuccess: (tempUrl) => {
        setImageToRender(tempUrl ?? undefined);
      },
      onError: (err) => {
        console.error('Erro ao resolver imagem do evento', err);
        setImageToRender(undefined);
      },
    });
  }, [event]);

  return (
    <Card
      sx={{
        height: '100%',
        p: 3,
        backgroundColor: '#202940',
        position: 'relative',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
      }}
    >
      <Grid2
        columnSpacing={2}
        sx={{
          display: 'flex',
          height: '75%',
          flexDirection: 'column',
        }}
      >
        <Grid2
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            margin: '1px',
            justifyContent: 'center',
          }}
        >
          {!imageToRender ? (
            <Skeleton variant="rounded" width={300} height={250} />
          ) : (
            <>
              <img
                width={300}
                height={250}
                src={imageToRender}
                alt="event_img"
                onClick={() =>
                  onClickInImage &&
                  onClickInImage(
                    event?.recognitionImage ||
                      event?.carImage ||
                      event?.image ||
                      '',
                  )
                }
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  cursor: 'pointer',
                  borderRadius: 8,
                }}
              />
            </>
          )}
        </Grid2>

        <Grid2
          container
          display={'flex'}
          justifyContent={'space-between'}
          width={'80%'}
          height={'25vh'}
          alignContent={'center'}
          padding={'35px'}
        >
          <Grid2 container>
            <Grid2 size={5}>
              <Typography
                variant={'h6'}
                sx={{ fontWeight: 'bold', color: '#4967a6' }}
              >
                DISPOSITIVO
              </Typography>
            </Grid2>
            <Grid2 size={10}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {event && event.device?.name}
              </Typography>
            </Grid2>
          </Grid2>
          {renderEventDetails()}
        </Grid2>
      </Grid2>
    </Card>
  );
};

export default LastEvent;
